import type { ContentKey, ResolvedConfig } from '@nvd/claim-shared';
import { formatText } from '@nvd/claim-shared';
import { createContext, useCallback, useContext, useMemo, useReducer, useState, type Dispatch } from 'react';
import { ApiError, type Api } from './api/client';
import { Shell } from './components/Shell';
import { UI } from './lib/ui';
import { initialState, reducer, type Action, type State } from './state/machine';
import { ClaimStatus } from './screens/ClaimStatus';
import { FindOrder } from './screens/FindOrder';
import { IssueDetails } from './screens/IssueDetails';
import { OrderItems } from './screens/OrderItems';
import { OtpVerify } from './screens/OtpVerify';
import { RefundConfirm, StoreCreditConfirm } from './screens/RefundConfirm';
import { ReorderConfirm } from './screens/ReorderConfirm';
import { Resolution } from './screens/Resolution';
import { Success } from './screens/Success';

export type TextFn = (key: ContentKey, vars?: Record<string, string | number>) => string;

interface PortalContextValue {
  config: ResolvedConfig;
  /** The shop's copy, with `{placeholders}` filled in. */
  t: TextFn;
  api: Api;
  state: State;
  dispatch: Dispatch<Action>;
}

const PortalContext = createContext<PortalContextValue | null>(null);

export function usePortal(): PortalContextValue {
  const value = useContext(PortalContext);
  if (!value) throw new Error('usePortal must be used inside <Portal>');
  return value;
}

/** Runs an API call with a busy flag and a user-facing error; an expired session sends the customer back to the start. */
export function useAction() {
  const { api, dispatch } = usePortal();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (task: () => Promise<void>) => {
      setBusy(true);
      setError(null);
      try {
        await task();
      } catch (err) {
        if (err instanceof ApiError && (err.code === 'SESSION_EXPIRED' || err.code === 'SESSION_REQUIRED')) {
          api.setSession(null);
          dispatch({ type: 'RESET', notice: err.message });
          return;
        }
        setError(err instanceof ApiError ? err.message : UI.genericError);
      } finally {
        setBusy(false);
      }
    },
    [api, dispatch]
  );

  return { busy, error, setError, run };
}

const SCREENS = {
  find: FindOrder,
  otp: OtpVerify,
  status: ClaimStatus,
  items: OrderItems,
  issue: IssueDetails,
  resolution: Resolution,
  reorder: ReorderConfirm,
  refund: RefundConfirm,
  storeCredit: StoreCreditConfirm,
  success: Success,
} as const;

/**
 * `initial` is for the editor's preview, which renders the real portal seeded to a chosen step.
 * `useReducer` reads it once, so the preview remounts (a `key`) to jump steps — while config edits
 * flow through context and re-render in place, keeping the merchant where they were.
 */
export function Portal({ config, api, initial }: { config: ResolvedConfig; api: Api; initial?: State }) {
  const [state, dispatch] = useReducer(reducer, initial ?? initialState);

  const t = useCallback<TextFn>((key, vars) => formatText(config.text[key], vars), [config]);
  const value = useMemo(() => ({ config, t, api, state, dispatch }), [config, t, api, state]);
  const Screen = SCREENS[state.step];

  return (
    <PortalContext.Provider value={value}>
      <Shell>
        <Screen key={state.step} />
      </Shell>
    </PortalContext.Provider>
  );
}
